import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { EXAM_CONFIG } from '../../utils/examSubjects';
import { parsePracticeExamDocx } from '../../utils/practiceExamDocx';
import {
  addQuestionsToBank,
  blankBankQuestion,
  buildExhibit,
  deleteAllBankQuestions,
  deleteBankQuestion,
  imagesFromExhibit,
  normalizeBankQuestion,
  parseQuestionBankJson,
  passageFromExhibit,
  saveBankQuestion,
  savePracticeExams,
  shufflePracticeExams,
  tablesFromExhibit,
  uploadQuestionImage,
} from '../../utils/questionBank';

const subjectIds = Object.keys(EXAM_CONFIG);

function examDraft(exam, index, subject) {
  const label = EXAM_CONFIG[subject]?.label || 'Exam';
  return {
    id: exam?.id,
    title: exam?.title || `${label} exam ${index + 1}`,
    timeMinutes: exam?.timeMinutes || 60,
    questionCount: exam?.questionCount || 25,
    questionIds: exam?.questionIds || [],
  };
}

export default function QuestionBankPanel() {
  const [subject, setSubject] = useState('science');
  const [questions, setQuestions] = useState([]);
  const [exams, setExams] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [examCount, setExamCount] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [editor, setEditor] = useState(null);

  useEffect(() => {
    setDirty(false);
    const unsubQuestions = onSnapshot(
      collection(db, 'questionBanks', subject, 'questions'),
      (snap) => {
        const rows = snap.docs.map((item) => ({ id: item.id, ...item.data() }));
        rows.sort((a, b) => (a.sourceNumber || 0) - (b.sourceNumber || 0) || String(a.stem).localeCompare(String(b.stem)));
        setQuestions(rows);
      },
      (err) => setError(err.message)
    );
    const unsubExams = onSnapshot(
      query(collection(db, 'practiceExams'), where('subject', '==', subject)),
      (snap) => {
        const rows = snap.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        setExams(rows);
      },
      (err) => setError(err.message)
    );
    return () => {
      unsubQuestions();
      unsubExams();
    };
  }, [subject]);

  useEffect(() => {
    if (dirty) return;
    setDrafts(exams.map((exam, index) => examDraft(exam, index, subject)));
    setExamCount(exams.length);
  }, [exams, dirty, subject]);

  const asked = useMemo(
    () => drafts.reduce((sum, draft) => sum + (Number(draft.questionCount) || 0), 0),
    [drafts]
  );

  function changeExamCount(value) {
    const count = Math.max(0, Math.min(20, Number(value) || 0));
    setExamCount(count);
    setDrafts((prev) => {
      const next = prev.slice(0, count).map((draft) => ({ ...draft }));
      while (next.length < count) next.push(examDraft(null, next.length, subject));
      return next;
    });
    setDirty(true);
  }

  function updateDraft(index, patch) {
    setDrafts((prev) => prev.map((draft, i) => (i === index ? { ...draft, ...patch } : draft)));
    setDirty(true);
  }

  function downloadSampleJson() {
    const sample = {
      questions: [
        {
          sourceNumber: 1,
          title: 'Photosynthesis Basics',
          skill: 'Reading for Meaning',
          domain: 'Life Science',
          passage:
            'Photosynthesis is the process by which green plants convert light energy into chemical energy stored in glucose.\n\n6CO₂ + 6H₂O + light energy → C₆H₁₂O₆ + 6O₂',
          stem: 'Which gas is RELEASED as a by-product of photosynthesis?',
          options: [
            { text: 'Carbon dioxide' },
            { text: 'Oxygen' },
            { text: 'Nitrogen' },
            { text: 'Hydrogen' },
          ],
          correct: 'B',
          explanation:
            'Plants release oxygen as a by-product. The equation shows 6O₂ produced on the right side.',
        },
        {
          sourceNumber: 2,
          title: "Newton's Second Law",
          skill: 'Using Numbers and Graphics',
          domain: 'Physical Science',
          passage: "Newton's Second Law: F = ma (force = mass × acceleration).",
          stem: 'A 10 kg cart is pushed with 30 N of force. What is the acceleration?',
          options: [
            { text: '0.33 m/s²' },
            { text: '3 m/s²' },
            { text: '20 m/s²' },
            { text: '300 m/s²' },
          ],
          correct: 'B',
          explanation: 'a = F ÷ m = 30 ÷ 10 = 3 m/s².',
        },
        {
          sourceNumber: 3,
          title: 'Linear Equations',
          skill: 'Using Numbers and Graphics',
          domain: 'Algebra',
          passage: '',
          stem: 'Solve for x:   3x − 7 = 14',
          options: [
            { text: 'x = 3' },
            { text: 'x = 7' },
            { text: 'x = 21' },
            { text: 'x = 2.33' },
          ],
          correct: 'B',
          explanation: 'Add 7 to both sides: 3x = 21. Divide by 3: x = 7.',
        },
        {
          sourceNumber: 4,
          title: 'Supply and Demand',
          skill: 'Reading for Meaning',
          domain: 'Economics',
          passage:
            'In a free market, when supply decreases but demand stays the same, prices tend to rise.',
          stem: 'A drought destroys much of the wheat crop. What is the most likely effect on bread prices?',
          options: [
            { text: 'Bread prices will fall because fewer people can afford it.' },
            { text: 'Bread prices will rise because wheat supply decreases.' },
            { text: 'Bread prices will remain unchanged.' },
            { text: 'Bread prices will fall because wheat becomes more valuable.' },
          ],
          correct: 'B',
          explanation: 'Reduced wheat supply with the same demand causes prices to rise.',
        },
      ],
    };
    const blob = new Blob([JSON.stringify(sample, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample-questions.json';
    a.click();
    URL.revokeObjectURL(url);
  }

  function downloadSampleDocx() {
    const a = document.createElement('a');
    a.href = '/sample-questions.docx';
    a.download = 'sample-questions.docx';
    a.click();
  }

  async function importDocx(file) {
    setBusy(true);
    setError('');
    setStatus('Reading the Word file…');
    try {
      const buffer = await file.arrayBuffer();
      const parsed = await parsePracticeExamDocx(buffer);
      if (!parsed.questions.length) throw new Error('No questions were found in that DOCX.');
      const added = await addQuestionsToBank(subject, parsed.questions, parsed.media, setStatus);
      setStatus(`Added ${added} questions to the ${EXAM_CONFIG[subject].label} bank.`);
    } catch (err) {
      setError(err.message || 'Could not import that DOCX.');
      setStatus('');
    } finally {
      setBusy(false);
    }
  }

  async function importJson(file) {
    setBusy(true);
    setError('');
    setStatus('Reading JSON…');
    try {
      const text = await file.text();
      const parsed = parseQuestionBankJson(text);
      const added = await addQuestionsToBank(subject, parsed, {}, setStatus);
      setStatus(`Added ${added} questions to the ${EXAM_CONFIG[subject].label} bank.`);
    } catch (err) {
      setError(err.message || 'Could not import that JSON file.');
      setStatus('');
    } finally {
      setBusy(false);
    }
  }

  async function onSaveSettings() {
    setBusy(true);
    setError('');
    try {
      await savePracticeExams(subject, drafts);
      setDirty(false);
      setStatus('Exam titles, times, and question counts are saved. Shuffle to assign questions.');
    } catch (err) {
      setError(err.message || 'Could not save exams.');
    } finally {
      setBusy(false);
    }
  }

  async function onShuffle() {
    setBusy(true);
    setError('');
    try {
      await shufflePracticeExams(subject, drafts, questions.map((item) => item.id));
      setDirty(false);
      setStatus('Questions were shuffled into these exams. No question is used twice.');
    } catch (err) {
      setError(err.message || 'Could not shuffle exams.');
    } finally {
      setBusy(false);
    }
  }

  async function onDeleteAll() {
    if (!questions.length) return;
    const label = EXAM_CONFIG[subject].label;
    const ok = window.confirm(`Delete all ${questions.length} questions from the ${label} bank? Exams for this subject will have no questions until you upload and shuffle again.`);
    if (!ok) return;
    setBusy(true);
    setError('');
    try {
      const removed = await deleteAllBankQuestions(subject, questions, setStatus);
      setStatus(`Deleted ${removed} questions from the ${label} bank.`);
    } catch (err) {
      setError(err.message || 'Could not delete the question bank.');
    } finally {
      setBusy(false);
    }
  }

  async function onDeleteQuestion(item) {
    const label = item.title || item.stem || 'this question';
    if (!window.confirm(`Delete “${label}” from the bank?`)) return;
    setError('');
    try {
      await deleteBankQuestion(subject, item.id);
    } catch (err) {
      setError(err.message || 'Could not delete that question.');
    }
  }

  return (
    <div style={{ flex: 1, padding: '24px 32px', overflowY: 'auto' }}>
      <div style={{ marginBottom: 18 }}>
        <h2 style={ui.title}>Question bank</h2>
        <p style={ui.sub}>
          Upload questions for a subject, then choose how many exams that subject has and how many questions each exam gets.
          Shuffle deals each question to only one exam. Students see that exam’s questions in a new order every attempt.
        </p>
      </div>

      <label style={ui.label}>Subject</label>
      <select
        value={subject}
        onChange={(event) => setSubject(event.target.value)}
        style={{ ...ui.input, maxWidth: 360, marginBottom: 16 }}
      >
        {subjectIds.map((id) => (
          <option key={id} value={id}>{EXAM_CONFIG[id].label}</option>
        ))}
      </select>

      <div style={ui.statRow}>
        <span style={ui.stat}>{questions.length} questions in the bank</span>
        <span style={ui.stat}>{exams.length} saved exams</span>
        <span style={ui.stat}>{asked} questions requested</span>
      </div>

      <div style={ui.buttonRow}>
        <label style={ui.fileBtn}>
          Upload DOCX
          <input
            type="file"
            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            hidden
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) importDocx(file);
            }}
          />
        </label>
        <label style={ui.fileBtn}>
          Upload JSON
          <input
            type="file"
            accept=".json,application/json"
            hidden
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) importJson(file);
            }}
          />
        </label>
        <button type="button" style={ui.fileBtn} disabled={busy} onClick={() => setEditor(blankBankQuestion())}>
          Add one question
        </button>
        <button type="button" style={ui.sampleBtn} onClick={downloadSampleJson} title="Download a sample JSON file showing the expected upload format">
          ↓ Sample JSON
        </button>
        <button type="button" style={ui.sampleBtn} onClick={downloadSampleDocx} title="Download a sample Word document showing the expected DOCX upload format">
          ↓ Sample DOCX
        </button>
      </div>
      <p style={ui.hint}>
        Not sure about the format?{' '}
        <button type="button" style={ui.inlineLink} onClick={downloadSampleJson}>Download a sample JSON</button>
        {' '}or{' '}
        <button type="button" style={ui.inlineLink} onClick={downloadSampleDocx}>Download a sample DOCX</button>
        {' '}to see exactly what to upload.
        JSON fields: <code style={{ fontSize: 11 }}>stem</code>, <code style={{ fontSize: 11 }}>passage</code>, <code style={{ fontSize: 11 }}>options</code>, <code style={{ fontSize: 11 }}>correct</code> (A–D), <code style={{ fontSize: 11 }}>explanation</code>, <code style={{ fontSize: 11 }}>title</code>, <code style={{ fontSize: 11 }}>skill</code>, <code style={{ fontSize: 11 }}>domain</code>.
      </p>

      {status ? <p style={ui.ok}>{status}</p> : null}
      {error ? <p style={ui.err}>{error}</p> : null}

      <section style={ui.card}>
        <h3 style={ui.cardTitle}>Exams for {EXAM_CONFIG[subject].label}</h3>
        <label style={ui.label}>How many exams</label>
        <input
          type="number"
          min="0"
          max="20"
          value={examCount}
          onChange={(event) => changeExamCount(event.target.value)}
          style={{ ...ui.input, maxWidth: 120 }}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 14 }}>
          {drafts.map((draft, index) => (
            <div key={draft.id || `new-${index}`} style={ui.examRow}>
              <input
                value={draft.title}
                onChange={(event) => updateDraft(index, { title: event.target.value })}
                placeholder="Exam title"
                style={{ ...ui.input, flex: 2 }}
              />
              <label style={ui.mini}>
                Minutes
                <input
                  type="number"
                  min="1"
                  value={draft.timeMinutes}
                  onChange={(event) => updateDraft(index, { timeMinutes: event.target.value })}
                  style={ui.input}
                />
              </label>
              <label style={ui.mini}>
                Questions
                <input
                  type="number"
                  min="1"
                  value={draft.questionCount}
                  onChange={(event) => updateDraft(index, { questionCount: event.target.value })}
                  style={ui.input}
                />
              </label>
              <span style={ui.dealt}>
                {(exams.find((exam) => exam.id === draft.id)?.questionIds || []).length} dealt
              </span>
            </div>
          ))}
          {drafts.length === 0 ? <p style={ui.hint}>Set how many exams this subject should have.</p> : null}
        </div>
        <div style={ui.buttonRow}>
          <button type="button" style={ui.secondary} disabled={busy} onClick={onSaveSettings}>Save exam settings</button>
          <button type="button" style={ui.primary} disabled={busy || drafts.length === 0} onClick={onShuffle}>Shuffle into exams</button>
        </div>
      </section>

      <section style={{ marginTop: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <h3 style={ui.cardTitle}>Bank questions</h3>
          <button type="button" style={ui.danger} disabled={busy || questions.length === 0} onClick={onDeleteAll}>
            Delete all questions
          </button>
        </div>
        <div style={ui.list}>
          {questions.length === 0 ? <p style={ui.hint}>No questions yet. Upload a DOCX or JSON file, or add one question.</p> : null}
          {questions.map((item, index) => (
            <div key={item.id} style={ui.qRow}>
              <div style={{ minWidth: 0 }}>
                <strong style={{ fontSize: 13, color: '#1b1b1d' }}>{index + 1}. {item.title || 'Untitled'}</strong>
                <p style={ui.qStem}>{item.stem}</p>
                <p style={ui.hint}>Answer {String.fromCharCode(65 + (Number(item.correct) || 0))}</p>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button type="button" style={ui.small} onClick={() => setEditor({ id: item.id, ...normalizeBankQuestion(item) })}>Edit</button>
                <button type="button" style={{ ...ui.small, color: '#b91c1c' }} onClick={() => onDeleteQuestion(item)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {editor ? (
        <QuestionEditor
          subject={subject}
          question={editor}
          busy={busy}
          onClose={() => setEditor(null)}
          onSave={async (question) => {
            setBusy(true);
            setError('');
            try {
              await saveBankQuestion(subject, question, editor.id);
              setEditor(null);
              setStatus(editor.id ? 'Question updated.' : 'Question added to the bank.');
            } catch (err) {
              setError(err.message || 'Could not save that question.');
            } finally {
              setBusy(false);
            }
          }}
        />
      ) : null}
    </div>
  );
}

function QuestionEditor({ subject, question, busy, onClose, onSave }) {
  const [form, setForm] = useState(() => ({
    ...blankBankQuestion(),
    ...question,
    passage: question.passage || passageFromExhibit(question.exhibit),
    images: imagesFromExhibit(question.exhibit),
    tables: tablesFromExhibit(question.exhibit),
    options: blankBankQuestion().options.map((option, index) => ({ ...option, ...(question.options?.[index] || {}) })),
  }));

  async function addImages(files, apply) {
    const uploaded = [];
    for (const file of files) uploaded.push(await uploadQuestionImage(subject, file));
    apply(uploaded.filter(Boolean));
  }

  return (
    <div style={ui.modalBackdrop}>
      <div style={ui.modal}>
        <h3 style={ui.cardTitle}>{question.id ? 'Edit question' : 'Add question'}</h3>
        <label style={ui.label}>Title</label>
        <input style={ui.input} value={form.title} onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))} />
        <label style={ui.label}>Passage</label>
        <textarea style={ui.area} rows={4} value={form.passage} onChange={(event) => setForm((prev) => ({ ...prev, passage: event.target.value }))} />
        <label style={ui.label}>Question</label>
        <textarea style={ui.area} rows={3} value={form.stem} onChange={(event) => setForm((prev) => ({ ...prev, stem: event.target.value }))} />
        <label style={ui.label}>Question images</label>
        <div style={ui.imageRow}>
          {form.images.map((image, index) => (
            <div key={image.path || image.url || index}>
              <img src={image.url} alt="" style={ui.thumb} />
              <button type="button" style={ui.small} onClick={() => setForm((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }))}>Remove</button>
            </div>
          ))}
        </div>
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={async (event) => {
            const files = [...(event.target.files || [])];
            event.target.value = '';
            await addImages(files, (uploaded) => setForm((prev) => ({ ...prev, images: [...prev.images, ...uploaded] })));
          }}
        />
        {form.tables?.length ? <p style={ui.hint}>{form.tables.length} table{form.tables.length === 1 ? '' : 's'} stay attached to this question.</p> : null}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
          {form.options.map((option, index) => (
            <div key={index} style={ui.optionBox}>
              <label style={ui.mini}>
                <input
                  type="radio"
                  name="correct-option"
                  checked={form.correct === index}
                  onChange={() => setForm((prev) => ({ ...prev, correct: index }))}
                />
                {' '}Option {String.fromCharCode(65 + index)}
              </label>
              <input
                style={ui.input}
                value={option.text}
                placeholder="Option text"
                onChange={(event) => setForm((prev) => ({
                  ...prev,
                  options: prev.options.map((item, i) => (i === index ? { ...item, text: event.target.value } : item)),
                }))}
              />
              {option.imageUrl ? <img src={option.imageUrl} alt="" style={ui.thumb} /> : null}
              <input
                type="file"
                accept="image/*"
                onChange={async (event) => {
                  const file = event.target.files?.[0];
                  event.target.value = '';
                  if (!file) return;
                  const uploaded = await uploadQuestionImage(subject, file);
                  setForm((prev) => ({
                    ...prev,
                    options: prev.options.map((item, i) => (i === index ? { ...item, imageUrl: uploaded.url, imagePath: uploaded.path } : item)),
                  }));
                }}
              />
            </div>
          ))}
        </div>
        <label style={ui.label}>Explanation</label>
        <textarea style={ui.area} rows={3} value={form.explanation} onChange={(event) => setForm((prev) => ({ ...prev, explanation: event.target.value }))} />
        <div style={ui.buttonRow}>
          <button type="button" style={ui.secondary} onClick={onClose}>Cancel</button>
          <button
            type="button"
            style={ui.primary}
            disabled={busy}
            onClick={() => onSave({
              ...form,
              exhibit: buildExhibit({ passage: form.passage, tables: form.tables, images: form.images }),
            })}
          >
            Save question
          </button>
        </div>
      </div>
    </div>
  );
}

const ui = {
  title: { fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 18, fontWeight: 800, color: '#1b1b1d', marginBottom: 4 },
  sub: { fontSize: 13, color: '#64748b', maxWidth: 720, lineHeight: 1.55, margin: 0 },
  label: { display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', margin: '10px 0 6px' },
  input: { width: '100%', boxSizing: 'border-box', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 13 },
  area: { width: '100%', boxSizing: 'border-box', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 13, fontFamily: 'inherit' },
  hint: { fontSize: 12, color: '#64748b', lineHeight: 1.5 },
  ok: { fontSize: 13, color: '#15803d', background: '#f0fdf4', borderRadius: 8, padding: '8px 12px' },
  err: { fontSize: 13, color: '#b91c1c', background: '#fef2f2', borderRadius: 8, padding: '8px 12px' },
  statRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 },
  stat: { fontSize: 12, fontWeight: 700, color: '#334155', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 999, padding: '4px 10px' },
  buttonRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 },
  fileBtn: { display: 'inline-flex', alignItems: 'center', background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', color: '#1b1b1d' },
  primary: { background: '#1B365D', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  secondary: { background: '#fff', color: '#334155', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  danger: { background: '#fff', color: '#b91c1c', border: '1.5px solid #fecaca', borderRadius: 10, padding: '8px 12px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  card: { marginTop: 18, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 14, padding: 16 },
  cardTitle: { fontSize: 15, fontWeight: 800, color: '#1b1b1d', margin: '0 0 8px' },
  examRow: { display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' },
  mini: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: '#64748b', width: 110 },
  dealt: { fontSize: 12, color: '#64748b', paddingBottom: 10 },
  list: { maxHeight: 480, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 },
  qRow: { display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px' },
  qStem: { margin: '4px 0 0', fontSize: 12, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 640 },
  small: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(15,33,55,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  modal: { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 720, maxHeight: '90vh', overflowY: 'auto', padding: 20 },
  optionBox: { border: '1px solid #e2e8f0', borderRadius: 10, padding: 10, display: 'flex', flexDirection: 'column', gap: 6 },
  imageRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  thumb: { width: 96, height: 72, objectFit: 'contain', background: '#f8fafc', borderRadius: 8, display: 'block' },
  sampleBtn: { display: 'inline-flex', alignItems: 'center', background: '#f0f9ff', border: '1.5px solid #bae6fd', borderRadius: 10, padding: '10px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', color: '#0369a1' },
  inlineLink: { background: 'none', border: 'none', padding: 0, fontSize: 12, fontWeight: 700, color: '#0369a1', cursor: 'pointer', textDecoration: 'underline' },
};
